import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import EvaluationPresentationModel, {
  TEvaluationPresentationModel
} from "../applicants/model/EvaluationPresentationModel";
import Application from "../../Application";
import { IEvaluation } from "../../model/evaluation/Evaluation";
import ExecuteReportEvaluationInteractor from "../../interactor/evaluation/ExecuteReportEvaluationInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import GetEvaluationsInteractor from "../../interactor/evaluation/GetEvaluationsInteractor";

export interface TFormVersionEvaluationsPresenter
  extends TLoadingAwarePresenter {
  evaluations: TEvaluationPresentationModel[];
  confirmEvaluationModalVisible: boolean;
  formVersionId: number;
}

export interface IFormVersionEvaluationsPresenter
  extends TFormVersionEvaluationsPresenter,
    TPresentable {
  setInitialData(formVersionId: number, evaluations?: IEvaluation[]): void;
  setFormVersionId(formVersionId: number): void;
  executeReportValidation(): void;
  showConfirmEvaluationModal(): void;
  hideConfirmEvaluationModal(): void;
}

const defaultState: TFormVersionEvaluationsPresenter = {
  evaluations: [] as TEvaluationPresentationModel[],
  confirmEvaluationModalVisible: false,
  formVersionId: 0
};

const FormVersionEvaluationsPresenter = withStore<
  IFormVersionEvaluationsPresenter,
  TFormVersionEvaluationsPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IFormVersionEvaluationsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TFormVersionEvaluationsPresenter>();
    loader.start("formVersionEvaluationsLoader");

    const setInitialData = (
      formVersionId: number,
      evaluations?: IEvaluation[]
    ) => {
      _store.update({
        formVersionId,
        evaluations: evaluations
          ? evaluations.map((evaluation) =>
              EvaluationPresentationModel(evaluation)
            )
          : []
      });
      loader.stop("formVersionEvaluationsLoader");
    };

    const setFormVersionId = (formVersionId: number) => {
      _store.update({ formVersionId });
    };

    const refreshEvaluations = async () => {
      loader.start("formVersionEvaluationsLoader");
      try {
        const { formVersionId } =
          _store.getState<IFormVersionEvaluationsPresenter>();
        const evaluations = await _application.container
          .resolve<GetEvaluationsInteractor>("getEvaluations")
          .execute(formVersionId);
        _store.update({
          evaluations: evaluations
            ? evaluations.map((evaluation) =>
                EvaluationPresentationModel(evaluation)
              )
            : []
        });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
      loader.stop("formVersionEvaluationsLoader");
    };

    const executeReportValidation = async () => {
      loader.start("executeReportValidationLoader");
      try {
        const { formVersionId } = _store.getState();
        await _application.container
          .resolve<ExecuteReportEvaluationInteractor>("executeReportEvaluation")
          .execute(formVersionId);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
      refreshEvaluations();
      loader.stop("executeReportValidationLoader");
    };

    const showConfirmEvaluationModal = () => {
      _store.update({ confirmEvaluationModalVisible: true });
    };

    const hideConfirmEvaluationModal = () => {
      _store.update({ confirmEvaluationModalVisible: false });
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setInitialData,
      setFormVersionId,
      executeReportValidation,
      showConfirmEvaluationModal,
      hideConfirmEvaluationModal
    };
  },
  defaultState
);

export default FormVersionEvaluationsPresenter;
